import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin, Calendar } from 'lucide-react';
import { menuData, practiceInfo } from '../data/siteData';
import './Header.css';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openMegaSubmenu, setOpenMegaSubmenu] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
    setOpenMegaSubmenu(null);
    // Scroll to top on route change
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setOpenMegaSubmenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownToggle = (menuId) => {
    setOpenDropdown(openDropdown === menuId ? null : menuId);
    setOpenMegaSubmenu(null);
  };

  const handleMegaSubmenuToggle = (submenuId) => {
    setOpenMegaSubmenu(openMegaSubmenu === submenuId ? null : submenuId);
  };

  // Single row menu items - excluding Welcome, Appointment Request, Privacy Policy, Terms and Conditions
  const navItems = menuData.mainMenu.filter(item => 
    ['Our Practice', 'Payment Options', 'Cosmetic Dentistry', 'Schedule Appointment', 'Procedures', 'Education and Resources', 'Contact Us'].includes(item.title)
  );

  return (
    <>
      {/* Top Action Bar */}
      <div className="top-action-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="top-bar-left">
              <a href={`tel:${practiceInfo.phone.replace(/[^0-9]/g, '')}`} className="top-action-item">
                <Phone size={16} />
                <span>{practiceInfo.phone}</span>
              </a>
              <a href={`mailto:${practiceInfo.email}`} className="top-action-item hide-mobile">
                <Mail size={16} />
                <span className="hide-mobile">{practiceInfo.email}</span>
              </a>
			
              <span className="top-action-item location-item hide-tablet">
			    <a href="https://www.google.com/maps?daddr=10000+E+Roosevelt+Blvd,Philadelphia,PA+19116" target="_blank" className="top-action-item">
                <MapPin size={16} />
                <span>{practiceInfo.address.city}, {practiceInfo.address.state}</span>
				</a>
              </span>
			  
            </div>
            <div className="top-bar-right">
              <Link to="/appointment-request" className="top-action-item book-btn">
                <Calendar size={16} />
                <span>Schedule Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`main-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            {/* Logo */}
				{/* <Link to="/" className="logo">
              <div className="logo-icon">
                <span>P3</span>
              </div>
              <div className="logo-text">
                <h1>P3 Dental</h1>
                <p>Northeast Philadelphia</p>
              </div>
				</Link>*/}
				
		{/* Prominent Schedule Appointment Button (Desktop)
             <Link to="/appointment-request" className="header-cta-btn hide-mobile">
              <Calendar size={20} />
              <span>Schedule Appointment</span>
             </Link> */}
			 
			<a href='/'><img src="/images/p3-dental-logo.jpg" alt="P3 Dental Logo" title="P3 Dental Logo"/></a>

            {/* Mobile Menu Toggle */}
            <button 
              className="menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>

            {/* Desktop Navigation - Single Row */}
            <nav className={`main-nav ${isMenuOpen ? 'active' : ''}`} ref={dropdownRef}>
            {/* Mobile Menu Close Button */}
              <button 
                className="mobile-menu-close"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
              <ul className="nav-list">
                {navItems.map(item => (
                  <li 
                    key={item.id} 
                    className={`nav-item ${item.hasSubmenu ? 'has-dropdown' : ''} ${item.isMegaMenu ? 'mega-menu-item' : ''}`}
                  >
                    {item.hasSubmenu ? (
                      <>
                        <div className="nav-link-wrapper">
                          <Link to={item.path} className="nav-link nav-link-main">
                            {item.title}
                          </Link>
                          <button 
                            className="dropdown-toggle"
                            onClick={() => handleDropdownToggle(item.id)}
                            aria-expanded={openDropdown === item.id}
                            aria-label={`Toggle ${item.title} submenu`}
                          >
                            <ChevronDown size={14} className={openDropdown === item.id ? 'rotated' : ''} />
                          </button>
                        </div>
                        <div className={`dropdown ${item.isMegaMenu ? 'mega-dropdown' : ''} ${openDropdown === item.id ? 'open' : ''}`}>
                          <div className="dropdown-inner">
                            {item.isMegaMenu ? (
                              <div className="mega-menu-grid">
                                {item.submenu.map(category => (
                                  <div key={category.id} className="mega-menu-section">
                                    {category.hasSubmenu ? (
                                      <>
                                        <div className="mega-section-header">
                                          <Link to={category.path} className="mega-section-title-link">
                                            {category.title}
                                          </Link>
                                          <button
                                            className="mega-toggle-btn"
                                            onClick={() => handleMegaSubmenuToggle(category.id)}
                                            aria-label={`Toggle ${category.title} submenu`}
                                          >
                                            <ChevronDown size={12} className={openMegaSubmenu === category.id ? 'rotated' : ''} />
                                          </button>
                                        </div>
                                        <div className={`mega-submenu ${openMegaSubmenu === category.id ? 'open' : ''}`}>
                                          {category.submenu.slice(0, 26).map(subItem => (
                                            <Link 
                                              key={subItem.id} 
                                              to={subItem.path}
                                              className="mega-link"
                                            >
                                              {subItem.title}
                                            </Link>
                                          ))}
                                          {category.submenu.length > 26 && (
                                            <Link to={category.path} className="mega-link more-link">
                                              View all →
                                            </Link>
                                          )}
                                        </div>
                                      </>
                                    ) : (
                                      <Link to={category.path} className="mega-section-link">
                                        {category.title}
                                      </Link>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              item.submenu.map(subItem => (
                                <Link 
                                  key={subItem.id} 
                                  to={subItem.path}
                                  className="dropdown-link"
                                >
                                  {subItem.title}
                                </Link>
                              ))
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <Link to={item.path} className="nav-link">
                        {item.title}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
}

export default Header;
